import React from 'react';
import { Search, Filter, RotateCcw, X } from 'lucide-react';
import { Input } from '@/components/common/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/ui/Select';

export interface FilterOption {
    value: string;
    label: string;
    icon?: React.ReactNode;
}

export interface FilterConfig {
    key: string;
    label: string;
    type: 'select' | 'search';
    options?: FilterOption[];
    placeholder?: string;
    icon?: React.ReactNode;
    width?: string;
}

interface CustomFilterProps {
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    filters: Record<string, any>;
    onFilterChange: (key: string, value: any) => void;
    onResetFilters: () => void;
    filterConfigs: FilterConfig[];
    advancedConfigs?: FilterConfig[];
    showAdvanced?: boolean;
    onToggleAdvanced?: () => void;
    hasActiveFilters?: boolean;
    className?: string;
    accentColor?: string;
}

const CustomFilter: React.FC<CustomFilterProps> = ({
    searchValue = '',
    onSearchChange,
    filters,
    onFilterChange,
    onResetFilters,
    filterConfigs,
    advancedConfigs = [],
    showAdvanced = false,
    onToggleAdvanced,
    hasActiveFilters = false,
    className = '',
    accentColor = '#50A970'
}) => {

    const renderFilter = (config: FilterConfig, isAdvanced = false) => {
        switch (config.type) {
            case 'search':
                return (
                    <div key={config.key} className="flex-1 min-w-0 cyber-input-wrapper">
                        <Input
                            type="search"
                            placeholder={config.placeholder || 'QUERY...'}
                            value={searchValue}
                            onChange={(e) => onSearchChange?.(e.target.value)}
                            leftIcon={config.icon || <Search className="h-4 w-4" />}
                            className="w-full h-10 bg-[#0A0B0E] border-[#1A1D24] text-white font-mono text-[12px] uppercase tracking-wider"
                        />
                    </div>
                );

            case 'select':
                return (
                    <div key={config.key} className={isAdvanced ? 'space-y-2' : 'flex-shrink-0'}>
                        {isAdvanced && (
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">
                                {config.label}
                            </label>
                        )}
                        <Select
                            value={filters[config.key] || ''}
                            onValueChange={(value) => onFilterChange(config.key, value)}
                        >
                            {/* FIX: Removed 'style' prop from SelectTrigger to resolve TS error */}
                            <SelectTrigger
                                className={`${isAdvanced ? 'w-full' : config.width || 'w-44'} h-10 bg-[#0A0B0E] border-[#1A1D24] text-slate-300 font-mono text-[11px] uppercase tracking-wider focus:ring-0 select-trigger-accent`}
                            >
                                <div className="flex items-center gap-2 truncate">
                                    {config.icon && <span className="text-slate-500">{config.icon}</span>}
                                    <SelectValue placeholder={config.placeholder || config.label} />
                                </div>
                            </SelectTrigger>
                            <SelectContent className="bg-[#0A0B0E] border-[#1A1D24] shadow-2xl">
                                {config.options?.map(option => (
                                    <SelectItem
                                        key={option.value}
                                        value={option.value}
                                        className="focus:bg-[#12151C] focus:text-white text-[11px] font-mono uppercase tracking-wider cursor-pointer"
                                    >
                                        <div className="flex items-center gap-2">
                                            {option.icon}
                                            {option.label}
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                );

            default:
                return null;
        }
    };

    const getActiveFilterBadges = () => {
        const badges = [];

        if (searchValue) {
            badges.push(
                <span key="search" className="inline-flex items-center gap-2 px-2 py-1 bg-[#12151C] border border-[#1A1D24] text-white rounded text-[10px] font-mono uppercase tracking-widest">
                    Search: "{searchValue}"
                    <button onClick={() => onSearchChange?.('')} className="hover:text-red-500 transition-colors">
                        <X className="h-3 w-3" />
                    </button>
                </span>
            );
        }

        [...filterConfigs, ...advancedConfigs].forEach(config => {
            const value = filters[config.key];
            if (value && value !== 'all' && value !== '') {
                const option = config.options?.find(opt => opt.value === value);
                badges.push(
                    <span key={config.key} className="inline-flex items-center gap-2 px-2 py-1 bg-[#12151C] border border-[#1A1D24] text-white rounded text-[10px] font-mono uppercase tracking-widest">
                        {config.label}: {option?.label || value}
                        <button
                            onClick={() => onFilterChange(config.key, 'all')}
                            className="hover:text-red-500 transition-colors"
                        >
                            <X className="h-3 w-3" />
                        </button>
                    </span>
                );
            }
        });

        return badges;
    };

    return (
        <div className={`bg-transparent p-0 space-y-5 font-mono ${className}`}>
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-end">
                <div className="flex flex-wrap flex-1 gap-3 min-w-0">
                    {filterConfigs.map(config => renderFilter(config))}
                </div>

                <div className="flex gap-2 flex-shrink-0">
                    {advancedConfigs.length > 0 && (
                        <button
                            onClick={onToggleAdvanced}
                            className={`flex items-center gap-2 px-4 h-10 text-[10px] font-bold uppercase tracking-widest rounded-lg border transition-all ${showAdvanced
                                    ? 'bg-white text-black border-white'
                                    : 'bg-[#12151C] text-slate-400 border-[#1A1D24] hover:text-white hover:border-slate-500'
                                }`}
                        >
                            <Filter className="h-3.5 w-3.5" />
                            Parameters
                        </button>
                    )}

                    {hasActiveFilters && (
                        <button
                            onClick={onResetFilters}
                            className="flex items-center gap-2 px-4 h-10 text-[10px] font-bold uppercase tracking-widest rounded-lg bg-[#12151C] text-slate-400 border border-[#1A1D24] hover:text-red-400 hover:border-red-400/50 transition-all"
                        >
                            <RotateCcw className="h-3.5 w-3.5" />
                            Reset
                        </button>
                    )}
                </div>
            </div>

            {showAdvanced && advancedConfigs.length > 0 && (
                <div className="border-t border-[#1A1D24] pt-5 animate-fade-in">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {advancedConfigs.map(config => renderFilter(config, true))}
                    </div>
                </div>
            )}

            {hasActiveFilters && (
                <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-[#1A1D24]">
                    <span className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Active Filters:</span>
                    <div className="flex flex-wrap gap-2">
                        {getActiveFilterBadges()}
                    </div>
                </div>
            )}

            {/* We move the accent logic here to avoid the TS error on the trigger component */}
            <style dangerouslySetInnerHTML={{
                __html: `
                .cyber-input-wrapper input:focus {
                    border-color: ${accentColor} !important;
                    box-shadow: 0 0 15px ${accentColor}20 !important;
                }
                .select-trigger-accent:focus, 
                .select-trigger-accent[data-state="open"] {
                    border-color: ${accentColor} !important;
                    box-shadow: 0 0 15px ${accentColor}20 !important;
                    color: white !important;
                }
            `}} />
        </div>
    );
};

export default CustomFilter;